// invoice/forms/ServiceForm.jsx
import React, { useState, useEffect } from "react";
import InvoiceField from "../component/InvoiceFiled";

const ServiceForm = ({ initialData = {} }) => {
  const [data, setData] = useState({
    invoiceNumber: "",
    date: "",
    clientName: "",
    clientAddress: "",
    balanceDue: "",
    taxRate: 18,
    items: [
      { id: 1, desc: "", subDesc: "", qty: 1, rate: 0, amount: 0 },
    ],
    ...initialData,
  });

  // Handle updates for simple fields
  const handleUpdate = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle updates for items
  const handleItemChange = (index, field, value) => {
    const newItems = [...data.items];
    if (field === "qty" || field === "rate") value = Number(value) || 0;
    newItems[index][field] = value;

    // Auto-calculate amount
    newItems[index].amount = (newItems[index].qty * newItems[index].rate).toFixed(2);
    setData((prev) => ({ ...prev, items: newItems }));
  };

  // Add / Remove item
  const addItem = () => {
    setData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: prev.items.length + 1, desc: "", subDesc: "", qty: 1, rate: 0, amount: 0 },
      ],
    }));
  };

  const removeItem = (index) => {
    const newItems = data.items.filter((_, i) => i !== index);
    setData((prev) => ({ ...prev, items: newItems }));
  };

  // Calculate subtotal, tax, total
  const subtotal = data.items.reduce((sum, i) => sum + Number(i.amount), 0);
  const tax = ((subtotal * data.taxRate) / 100).toFixed(2);
  const total = (subtotal + Number(tax)).toFixed(2);

  // Save to localStorage
  const handleSave = () => {
    const savedData = { ...data, subtotal, tax, total };
    localStorage.setItem("serviceInvoiceData", JSON.stringify(savedData));
    alert("Invoice saved locally!");
  };

  return (
    <div className="space-y-6 p-4 border rounded-md">
      <h3 className="font-bold text-lg text-[#A83E0C] border-b pb-2">Service Invoice</h3>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <InvoiceField
          label="Invoice #"
          value={data.invoiceNumber}
          onChange={(v) => handleUpdate("invoiceNumber", v)}
          required
        />
        <InvoiceField
          label="Invoice Date"
          type="date"
          value={data.date}
          onChange={(v) => handleUpdate("date", v)}
        />
      </div>

      <InvoiceField
        label="Client Name"
        value={data.clientName}
        onChange={(v) => handleUpdate("clientName", v)}
        required
      />
      <InvoiceField
        label="Client Address"
        value={data.clientAddress}
        onChange={(v) => handleUpdate("clientAddress", v)}
      />

      {/* Items */}
      <h4 className="font-bold text-sm text-gray-600">Items</h4>
      <table className="w-full text-sm border-collapse mb-2">
        <thead>
          <tr className="bg-[#A83E0C] text-white">
            <th className="p-1">#</th>
            <th className="p-1">Description</th>
            <th className="p-1">Sub Description</th>
            <th className="p-1">Qty</th>
            <th className="p-1">Rate (₹)</th>
            <th className="p-1">Amount (₹)</th>
            <th className="p-1">Action</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, idx) => (
            <tr key={idx} className="border-b">
              <td className="p-1 text-center">{idx + 1}</td>
              <td className="p-1">
                <input
                  type="text"
                  value={item.desc}
                  onChange={(e) => handleItemChange(idx, "desc", e.target.value)}
                  className="w-full border px-1"
                />
              </td>
              <td className="p-1">
                <input
                  type="text"
                  value={item.subDesc}
                  onChange={(e) => handleItemChange(idx, "subDesc", e.target.value)}
                  className="w-full border px-1"
                />
              </td>
              <td className="p-1">
                <input
                  type="number"
                  value={item.qty}
                  onChange={(e) => handleItemChange(idx, "qty", e.target.value)}
                  className="w-full border px-1 text-right"
                />
              </td>
              <td className="p-1">
                <input
                  type="number"
                  value={item.rate}
                  onChange={(e) => handleItemChange(idx, "rate", e.target.value)}
                  className="w-full border px-1 text-right"
                />
              </td>
              <td className="p-1 text-right">{item.amount}</td>
              <td className="p-1 text-center">
                <button
                  onClick={() => removeItem(idx)}
                  className="text-red-500 font-bold"
                  disabled={data.items.length === 1}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        onClick={addItem}
        className="bg-[#A83E0C] text-white px-3 py-1 rounded text-sm"
      >
        Add Item
      </button>

      {/* Summary */}
      <div className="mt-4 space-y-1 text-sm">
        <div className="flex justify-between">
          <span>Subtotal:</span>
          <span>₹{subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>GST ({data.taxRate}%):</span>
          <span>₹{tax}</span>
        </div>
        <div className="flex justify-between font-bold text-lg border-t pt-1">
          <span>Total:</span>
          <span>₹{total}</span>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-4">
        <button
          onClick={handleSave}
          className="bg-[#A83E0C] text-white px-4 py-2 rounded hover:bg-[#8A320B]"
        >
          Save Invoice
        </button>
      </div>
    </div>
  );
};

export default ServiceForm;
